import { TAppDispatch, TGetState } from 'store/types'
import { getAiVideoModels_API, getAIVideoProjects_API } from 'api'
import {
  AIVideoSocketMessageStatus,
  IAIVideoGenerationParams,
  IAIVideoGenerationRequestBody,
  IAIVideoListParams
} from 'types/aiVideo'
import slice from 'store/features/aiVideo/slice'
import { aiVideoSelectors } from 'store/features/aiVideo/selectors'
import {
  buildAIVideoGenerationRequest,
  checkAndShiftMediaItems,
  getDefaultAIVideoModel,
  getDefaultReferenceMode,
  findMatchingOption,
  getBestFreeQualityOption
} from '../utils'
import { ErrorKeys } from 'consts/errorKeys'
import { captureException } from '@sentry/react'
import { ApiError, ReduxThunkError } from 'utils/errors'
import { projectSelectors } from 'store/features/project/selectors'
import { formThunks } from 'store/features/aiVideo/thunks/form'
import { animateThunks } from 'store/features/aiVideo/thunks/animate'
import { smartGenerationThunks } from 'store/features/aiVideo/thunks/generation'
import { uploadThunks } from 'store/features/aiVideo/thunks/upload'
import { projectDataSelectors } from 'store/features/projectData/selectors'
import { handleApiError } from 'store/utils'
import { isSmartEditModeSelector } from 'store/selectors'
import { sortArraysByKey } from 'store/features/projectData/utils'
import { getVerifiedModel } from './utils'
import { versionHistoryThunks } from './versionHistory'
import { getAIVideoUserPreferences } from 'utils/localStorage'
import { userSelectors } from 'store/features/user/selectors'
import { MIN_PREMIUM_QUALITY } from 'consts'

const { actions } = slice

interface IUpdateSelectedModelParams {
  modelName: string
  duration?: number
  quality?: number
  aspectRatio?: string | null
}
const updateSelectedModel =
  ({ modelName, duration, quality, aspectRatio }: IUpdateSelectedModelParams) =>
  async (dispatch: TAppDispatch, getState: TGetState) => {
    const state = getState()
    const model = getVerifiedModel(modelName, state.aiVideo.aiVideoModels)
    const multiRefImagesBucket = aiVideoSelectors.multiRefImagesBucket(state)
    const framesBucket = aiVideoSelectors.framesBucket(state)
    const hasOnlyFramesBucket =
      framesBucket.length > 0 && !multiRefImagesBucket.length

    const { duration: defaultDuration, quality: defaultQuality } =
      model.defaultValues

    const selectedQuality = quality || aiVideoSelectors.quality(state)
    const selectedDuration = duration || aiVideoSelectors.duration(state)

    dispatch(actions.setSelectedModel(model.model))
    // Find matching options or fallback to defaults (first item)
    const matchingDurationOption = findMatchingOption({
      items: model.options,
      key: 'duration',
      selectedValue: selectedDuration,
      defaultValue: defaultDuration
    })

    const matchingQualityOption = findMatchingOption({
      items: matchingDurationOption.qualities,
      key: 'quality',
      selectedValue: selectedQuality,
      defaultValue: defaultQuality
    })

    const hasImage = aiVideoSelectors.hasUploadedImage(state)
    const currentAspectRatio = aiVideoSelectors.aspectRatio(state)
    const resolvedAspectRatio =
      aspectRatio !== undefined ? aspectRatio : currentAspectRatio

    const isMediaResizingSupported =
      model.capabilities.isMediaResizingSupported === true

    if (
      aspectRatio !== undefined ||
      !hasImage ||
      resolvedAspectRatio ||
      isMediaResizingSupported
    ) {
      const projectAspectRatio =
        projectDataSelectors.defaultDimensions(state)?.aspectRatioString
      const availableRatios = model.capabilities.aspectRatios
      if (availableRatios?.length) {
        const matchingAspectRatio =
          availableRatios.find(ratio => ratio === resolvedAspectRatio) ||
          availableRatios.find(ratio => ratio === projectAspectRatio) ||
          availableRatios[0]
        dispatch(actions.setAspectRatio(matchingAspectRatio))
      }
    }

    // Clamp quality for free users to below premium threshold
    const isFreeUser = userSelectors.isFreeUser(state)
    const finalQualityOption =
      isFreeUser && matchingQualityOption.quality >= MIN_PREMIUM_QUALITY
        ? (getBestFreeQualityOption(matchingDurationOption.qualities) ??
          matchingQualityOption)
        : matchingQualityOption

    // Update store with matched or default values
    dispatch(actions.setDuration(matchingDurationOption.duration))
    dispatch(actions.setQuality(finalQualityOption.quality))
    dispatch(actions.setVideoGenerationCost(finalQualityOption.credit))
    dispatch(
      actions.setReferenceMode(
        getDefaultReferenceMode(model, hasOnlyFramesBucket)
      )
    )
    dispatch(actions.applyDefaultImageAspectRatio())
  }

const getPreparedRequestPayload =
  (projectId?: number) =>
  (
    _dispatch: TAppDispatch,
    getState: TGetState
  ): IAIVideoGenerationRequestBody => {
    const state = getState()
    const isConsistencyEnabled = aiVideoSelectors.isConsistencyEnabled(state)
    const isSmartEditMode = isSmartEditModeSelector(state)
    const projectMeta = projectSelectors.meta(state)
    const genAiProjectId = projectMeta?.genAiProjectId
    const refImageSelectedStyleId =
      aiVideoSelectors.refImageSelectedStyleId(state)

    const selectedModel = aiVideoSelectors.selectedModel(state)
    const hasImage = aiVideoSelectors.hasUploadedImage(state)
    const currentAspectRatio = aiVideoSelectors.aspectRatio(state)

    const aspectRatioForRequest = hasImage
      ? (currentAspectRatio ?? undefined)
      : currentAspectRatio || '16:9'

    const targetMediaItemId = aiVideoSelectors.targetMediaItemId(state)

    const parentProjectId =
      aiVideoSelectors.generationParams(state).parentProjectId

    return buildAIVideoGenerationRequest({
      id: isConsistencyEnabled || isSmartEditMode ? genAiProjectId : undefined,
      referenceMedias: aiVideoSelectors.apiReferenceMedias(state),
      prompt: isSmartEditMode
        ? aiVideoSelectors.smartPrompt(state)
        : aiVideoSelectors.prompt(state),
      videoDurationSeconds: aiVideoSelectors.duration(state) || 0,
      videoModel: selectedModel.model || '',
      aspectRatio: aspectRatioForRequest,
      quality: aiVideoSelectors.quality(state),
      aiImageId: aiVideoSelectors.aiImageId(state) || null,
      projectId,
      styleId: isSmartEditMode ? undefined : refImageSelectedStyleId,
      timelineItemId: targetMediaItemId,
      parentProjectId: isConsistencyEnabled ? undefined : parentProjectId
    })
  }

// Async thunks

const checkAndShiftMediaItemsForRegeneration =
  (selectedItemId: string, newDuration: number) =>
  (dispatch: TAppDispatch, getState: TGetState) => {
    const state = getState()
    const mediaItems = projectDataSelectors.mediaItems(state)
    const sortedMediaItems = sortArraysByKey('startTime', mediaItems)

    const selectedItemIndex = sortedMediaItems.findIndex(
      item => item.id === selectedItemId
    )

    if (selectedItemIndex === -1) {
      return { success: false, error: 'Selected item index not found' }
    }

    const selectedItem = sortedMediaItems[selectedItemIndex]
    const newItemEndTime = selectedItem.startTime + newDuration

    // Check space availability and shift items if needed
    const shiftResult = checkAndShiftMediaItems({
      mediaItems: sortedMediaItems,
      selectedItemIndex,
      newItemEndTime
    })

    if (shiftResult.error) {
      dispatch(handleApiError(ErrorKeys.GENERAL_API_ERROR))

      return { success: false, error: shiftResult.error }
    }

    return { success: true, shifted: false }
  }

const getAIVideoHistory =
  (params: IAIVideoListParams = {}) =>
  async (dispatch: TAppDispatch, getState: TGetState) => {
    const state = getState()
    const { aiVideoHistory } = state.aiVideo
    const currentProjectId = projectSelectors.id(state)

    const isScopedWithoutProject =
      aiVideoHistory.isScopedToCurrentProject && !currentProjectId

    if (isScopedWithoutProject) {
      return
    }

    try {
      dispatch(actions.setAiVideoHistoryLoading(true))

      const response = await getAIVideoProjects_API({
        ...params,
        videoProjectId: aiVideoHistory.isScopedToCurrentProject
          ? Number(currentProjectId)
          : undefined
      })

      const successfulProjects = response.data.filter(
        item => item.status !== AIVideoSocketMessageStatus.FAILED
      )

      // update pagination metadata
      dispatch(actions.setAiVideoHistoryTotalCount(response.totalCount!))
      dispatch(
        actions.setAiVideoHistoryRawOffset(params.offset! + params.limit!)
      )

      const isInitialFetch = params.offset === 0
      const currentHistory = aiVideoSelectors.historyList(state) ?? []

      if (isInitialFetch) {
        // Preserve locally-added processing videos not yet in API response
        const pendingGeneratingVideos = currentHistory.filter(
          item =>
            item.status === AIVideoSocketMessageStatus.PROCESSING &&
            !successfulProjects.some(project => project.id === item.id)
        )
        dispatch(
          actions.setAiVideoHistory([
            ...pendingGeneratingVideos,
            ...successfulProjects
          ])
        )
      } else {
        // Append paginated results to existing history
        dispatch(
          actions.setAiVideoHistory([...currentHistory, ...successfulProjects])
        )
      }
    } catch (error) {
      if (!(error instanceof ApiError)) {
        captureException(new ReduxThunkError(error))
      }
      dispatch(handleApiError(ErrorKeys.GENERAL_API_ERROR))
    } finally {
      dispatch(actions.setAiVideoHistoryLoading(false))
    }
  }

const getAIVideoModels =
  () => async (dispatch: TAppDispatch, getState: TGetState) => {
    const state = getState()
    const existingModels = aiVideoSelectors.aiVideoModels(state)
    const isLoadingTabData = aiVideoSelectors.isLoadingTabData(state)

    if (existingModels.length > 0 || isLoadingTabData) {
      return
    }

    try {
      dispatch(actions.setIsLoadingTabData(true))
      const response = await getAiVideoModels_API()
      const fetchedModels = response.data
      const aiFlowVideoModel = projectSelectors.aiFlowVideoModelCode(state)

      dispatch(actions.setAIVideoModels(fetchedModels))

      if (fetchedModels.length === 0) {
        return
      }

      // Determine which model to select: query param > default
      const urlSearchParams = new URLSearchParams(window.location.search)
      const modelCodeFromQuery = urlSearchParams.get('aiVideoModel')

      const modelFromQuery = modelCodeFromQuery
        ? fetchedModels.find(model => model.model === modelCodeFromQuery)?.model
        : null

      const selectedModel =
        aiFlowVideoModel ??
        modelFromQuery ??
        getDefaultAIVideoModel(fetchedModels).model

      const userPreferences = getAIVideoUserPreferences()
      const defaultQuality = fetchedModels.find(
        model => model.model === selectedModel
      )?.defaultValues.quality

      dispatch(
        updateSelectedModel({
          modelName: selectedModel,
          quality: userPreferences.quality ?? defaultQuality
        })
      )
    } catch (error) {
      if (!(error instanceof ApiError)) {
        captureException(new ReduxThunkError(error))
      }
      dispatch(handleApiError(ErrorKeys.GENERAL_API_ERROR))
    } finally {
      dispatch(actions.setIsLoadingTabData(false))
    }
  }

const saveCurrentSettingsAsDefault =
  () => (_dispatch: TAppDispatch, getState: TGetState) => {
    const state = getState()
    const { selectedModel, duration, aspectRatio, quality } =
      state.aiVideo.generationParams
    _dispatch(
      actions.setDefaultGenerationSettings({
        selectedModel,
        duration,
        aspectRatio,
        quality
      })
    )
  }

const updateModelCreditsPerDuration =
  (params: {
    modelCode: string | null
    duration?: number
    quality?: number | null
  }) =>
  (dispatch: TAppDispatch, getState: TGetState) => {
    const { modelCode, duration, quality } = params
    if (!modelCode) {
      return
    }

    const state = getState()
    const model = state.aiVideo.aiVideoModels.find(m => m.model === modelCode)

    if (!model) {
      return
    }

    const { duration: defaultDuration, quality: defaultQuality } =
      model.defaultValues

    // Use provided values or fallback to defaults (first item)
    const targetDuration = duration || defaultDuration
    const targetQuality = quality || defaultQuality

    const matchingDurationOption = findMatchingOption({
      items: model.options,
      key: 'duration',
      selectedValue: targetDuration,
      defaultValue: defaultDuration
    })

    const matchingQualityOption = findMatchingOption({
      items: matchingDurationOption.qualities,
      key: 'credit',
      selectedValue: targetQuality,
      defaultValue: defaultQuality
    })

    // Clamp quality for free users to below premium threshold
    const isFreeUser = userSelectors.isFreeUser(state)
    const bestFree =
      isFreeUser && targetQuality >= MIN_PREMIUM_QUALITY
        ? getBestFreeQualityOption(matchingDurationOption.qualities)
        : null
    const finalQuality = bestFree?.quality ?? targetQuality
    const finalCredit = bestFree?.credit ?? matchingQualityOption.credit

    if (!finalCredit) {
      return
    }

    // Update store with resolved values
    dispatch(actions.setSelectedModel(modelCode))
    dispatch(actions.setDuration(targetDuration))
    dispatch(actions.setQuality(finalQuality))
    dispatch(actions.setVideoGenerationCost(finalCredit))
  }

const setGenerationParams =
  (params: IAIVideoGenerationParams) => (dispatch: TAppDispatch) => {
    dispatch(actions.setGenerationParams(params))
    dispatch(
      updateModelCreditsPerDuration({
        modelCode: params.selectedModel,
        duration: params.duration,
        quality: params.quality
      })
    )
  }

export const aiVideoThunks = {
  ...actions,
  ...formThunks,
  ...animateThunks,
  ...smartGenerationThunks,
  ...uploadThunks,
  ...versionHistoryThunks,
  setGenerationParams,
  getPreparedRequestPayload,
  checkAndShiftMediaItemsForRegeneration,
  getAIVideoHistory,
  getAIVideoModels,
  updateSelectedModel,
  updateModelCreditsPerDuration,
  saveCurrentSettingsAsDefault
}
